import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, X, Loader2, CreditCard, ArrowLeft, Phone, Mail, Lock, AlertTriangle, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { bookingsApi, paymentsApi } from '@/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BookingPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    const fetchBooking = async () => {
      try {
        const data = await bookingsApi.getById(id);
        setBooking(data);
        
        // If booking is confirmed/paid, try to fetch contact details
        const canViewContact = ['confirmed', 'paid', 'in_progress', 'completed'].includes(data.status) &&
          (data.payment_status === 'paid' || data.payment_status === 'held' || data.payment_status === 'not_required');
        
        if (canViewContact) {
          try {
            const contactData = await bookingsApi.getContactDetails(id);
            setContactInfo(contactData);
          } catch (err) {
            console.log('Contact info not available:', err);
          }
        }
      } catch (error) {
        toast.error(t('common.error'));
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [id, isAuthenticated, navigate, t]);

  const handlePayment = async () => {
    setPaymentLoading(true);
    try {
      const { url } = await paymentsApi.createCheckout(booking.booking_id);
      window.location.href = url;
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!booking) return null;

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-green-100 text-green-800',
    completed: 'bg-blue-100 text-blue-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  const paymentStatusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-green-100 text-green-800',
    refunded: 'bg-gray-100 text-gray-800'
  };

  return (
    <main className="min-h-screen pt-20 pb-12" data-testid="booking-page">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back')}
        </Button>

        <div className="bg-card rounded-2xl border p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">{t('booking.title')}</h1>
            <div className="flex gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[booking.status]}`}>
                {t(`booking.status.${booking.status}`)}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${paymentStatusColors[booking.payment_status]}`}>
                {booking.payment_status === 'paid' ? 'Payé' : 'Non payé'}
              </span>
            </div>
          </div>

          <div className="space-y-6">
            {/* Experience Info */}
            <div className="border-b pb-6">
              <h2 className="font-semibold text-lg mb-2">{booking.experience_title}</h2>
              <div className="text-muted-foreground space-y-1">
                <p>Explorateur: {booking.explorer_name}</p>
                <p>Date: {new Date(booking.date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', { dateStyle: 'long' })}</p>
                <p>Participants: {booking.participants}</p>
              </div>
            </div>

            {/* Price Breakdown */}
            <div className="space-y-3">
              <h3 className="font-semibold">{t('booking.summary')}</h3>
              
              <div className="flex justify-between">
                <span>{t('booking.base_price')}</span>
                <span>{(booking.base_price || 0).toFixed(2)}€</span>
              </div>
              
              {booking.include_vehicle && (booking.vehicle_price || 0) > 0 && (
                <div className="flex justify-between">
                  <span>{t('booking.vehicle')}</span>
                  <span>{(booking.vehicle_price || 0).toFixed(2)}€</span>
                </div>
              )}
              
              {booking.include_accommodation && (booking.accommodation_price || 0) > 0 && (
                <div className="flex justify-between">
                  <span>{t('booking.accommodation')}</span>
                  <span>{(booking.accommodation_price || 0).toFixed(2)}€</span>
                </div>
              )}
              
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>{t('booking.platform_fee')} (15%)</span>
                <span>{(booking.platform_fee || 0).toFixed(2)}€</span>
              </div>
              
              <div className="border-t pt-3 flex justify-between text-lg font-bold">
                <span>{t('booking.total')}</span>
                <span className="text-primary">{(booking.total_price || 0).toFixed(2)}€</span>
              </div>
            </div>

            {/* Message */}
            {booking.message && (
              <div className="border-t pt-6">
                <h3 className="font-semibold mb-2">Message</h3>
                <p className="text-muted-foreground">{booking.message}</p>
              </div>
            )}

            {/* Contact Info Section */}
            {contactInfo ? (
              <div className="border-t pt-6" data-testid="contact-info-section">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  {i18n.language === 'fr' ? 'Coordonnées du guide' : 'Guide Contact Info'}
                </h3>
                <div className="bg-green-50 rounded-xl p-4 space-y-3">
                  <p className="text-sm text-green-700 mb-3">
                    {contactInfo.message}
                  </p>
                  {contactInfo.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-green-600" />
                      <a href={`mailto:${contactInfo.email}`} className="text-green-800 hover:underline">
                        {contactInfo.email}
                      </a>
                    </div>
                  )}
                  {contactInfo.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-green-600" />
                      <a href={`tel:${contactInfo.phone}`} className="text-green-800 hover:underline">
                        {contactInfo.phone}
                      </a>
                    </div>
                  )}
                  {contactInfo.social_links?.website && (
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-green-600" />
                      <a href={contactInfo.social_links.website} target="_blank" rel="noopener noreferrer" className="text-green-800 hover:underline">
                        {contactInfo.social_links.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ) : booking.status !== 'cancelled' && (
              <div className="border-t pt-6" data-testid="contact-locked-section">
                <div className="bg-amber-50 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-amber-800 mb-1">
                        {i18n.language === 'fr' ? 'Coordonnées verrouillées' : 'Contact Info Locked'}
                      </h3>
                      <p className="text-sm text-amber-700">
                        {booking.payment_status !== 'paid' && booking.payment_status !== 'held' && booking.payment_status !== 'not_required'
                          ? (i18n.language === 'fr' 
                              ? 'Effectuez le paiement pour accéder aux coordonnées du guide.' 
                              : 'Complete payment to access guide contact information.')
                          : (i18n.language === 'fr'
                              ? 'Les coordonnées seront disponibles une fois la réservation confirmée.'
                              : 'Contact information will be available once the booking is confirmed.')
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Button */}
            {booking.payment_status !== 'paid' && booking.status !== 'cancelled' && (
              <Button
                onClick={handlePayment}
                disabled={paymentLoading}
                className="w-full bg-accent hover:bg-accent/90 text-accent-foreground py-6 text-lg btn-accent-glow"
                data-testid="pay-btn"
              >
                {paymentLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    {t('booking.pay')} - {(booking.total_price || 0).toFixed(2)}€
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [checking, setChecking] = useState(true);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/dashboard');
      return;
    }

    const pollStatus = async (attempts = 0) => {
      if (attempts >= 5) {
        setChecking(false);
        setStatus('timeout');
        return;
      }

      try {
        const result = await paymentsApi.getStatus(sessionId);
        if (result.payment_status === 'paid') {
          setStatus('success');
          setChecking(false);
          toast.success('Paiement réussi !');
        } else {
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      } catch (error) {
        setTimeout(() => pollStatus(attempts + 1), 2000);
      }
    };

    pollStatus();
  }, [searchParams, navigate]);

  return (
    <main className="min-h-screen pt-20 flex items-center justify-center" data-testid="payment-success-page">
      <div className="text-center max-w-md mx-auto px-4">
        {checking ? (
          <>
            <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Vérification du paiement...</h1>
            <p className="text-muted-foreground">Veuillez patienter</p>
          </>
        ) : status === 'success' ? (
          <>
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Paiement réussi !</h1>
            <p className="text-muted-foreground mb-6">
              Votre réservation est confirmée. L'explorateur vous contactera bientôt.
            </p>
            <Button onClick={() => navigate('/dashboard')}>
              Voir mes réservations
            </Button>
          </>
        ) : (
          <>
            <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
              <X className="w-10 h-10 text-yellow-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Vérification en cours</h1>
            <p className="text-muted-foreground mb-6">
              Nous vérifions votre paiement. Vous recevrez un email de confirmation.
            </p>
            <Button onClick={() => navigate('/dashboard')}>
              Retour au dashboard
            </Button>
          </>
        )}
      </div>
    </main>
  );
};

const PaymentCancelPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bookingId = searchParams.get('booking_id');

  return (
    <main className="min-h-screen pt-20 flex items-center justify-center" data-testid="payment-cancel-page">
      <div className="text-center max-w-md mx-auto px-4">
        <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
          <X className="w-10 h-10 text-red-600" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Paiement annulé</h1>
        <p className="text-muted-foreground mb-6">
          Votre paiement a été annulé. Vous pouvez réessayer ou modifier votre réservation.
        </p>
        <div className="flex gap-4 justify-center">
          {bookingId && (
            <Button onClick={() => navigate(`/booking/${bookingId}`)}>
              Réessayer
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Retour au dashboard
          </Button>
        </div>
      </div>
    </main>
  );
};

export { BookingPage, PaymentSuccessPage, PaymentCancelPage };
